import { nothing } from 'lit'
import { AsyncDirective } from 'lit/async-directive.js'
import { directive, type ElementPart } from 'lit/directive.js'
import { animationFrameScheduler, BehaviorSubject, EMPTY, fromEvent, Subject, timer } from 'rxjs'
import { distinctUntilChanged, observeOn, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators'
import { SPRING_SNAPPY } from '@schmancy/utils/animation'
import { type DropAxis, hideDropIndicator, showDropIndicator } from './drop-indicator'
import { reducedMotion$ } from './reduced-motion'

export type SchmancyDropEvent = CustomEvent<{
	source: string
	destination: string
	/**
	 * Which side of the destination the item was dropped on, measured on the axis
	 * that destination's own list runs along.
	 *
	 * This pair answers a question about ONE destination — it carries no order, so
	 * it cannot say which place in a sequence the drop meant. A list whose order
	 * is the point, and every wrapping grid or strip, belongs to `reorder()`
	 * instead: that directive holds the ordered ids, computes the insertion index
	 * against them, and is the only path a keyboard or an automation harness has
	 * into reordering at all.
	 */
	position?: 'before' | 'after'
}>

/** Current drag source ID — single source of truth for all drag/drop directives */
const dragSource$ = new BehaviorSubject<string | null>(null)

/** Insertion line position — drives the singleton line element reactively */
const linePosition$ = new BehaviorSubject<{
	target: HTMLElement
	destinationId: string
	position: 'before' | 'after'
	axis: DropAxis
} | null>(null)

/** Registry of all drag-enabled elements by ID (for direction detection + FLIP) */
const dragRegistry = new Map<string, HTMLElement>()

/** Pre-drop rect snapshots for FLIP animation (captured at dragstart) */
const preDropRects = new Map<string, DOMRect>()

// Single global subscription (not per-directive) — drives the shared insertion
// line. Reduced motion drops the tween between positions inside the indicator,
// never the line itself: a user who asked for less motion still has to be told
// where the item lands.
linePosition$
	.pipe(distinctUntilChanged((a, b) => a?.target === b?.target && a?.position === b?.position && a?.axis === b?.axis))
	.subscribe(state => {
		if (!state) return hideDropIndicator()
		showDropIndicator(state.target, state.position, state.axis)
	})

/**
 * Which way the order runs through the list `targetEl` sits in, read off that
 * list's own layout.
 *
 * Source-vs-target geometry answers a different question — are these two on the
 * same visual row — and in a wrapping flow its answer changes as the pointer
 * crosses a row boundary, so one gesture over one tile resolves on the inline
 * axis inside the source's row and on the block axis outside it, and the two
 * disagree about which half of that tile means anything. A list's layout does
 * not change mid-gesture; where the dragged row happens to sit does.
 */
function axisOf(targetEl: HTMLElement): DropAxis {
	const parent = targetEl.parentElement
	if (!parent) return 'down'
	const registered = new Set(dragRegistry.values())
	const [first, second] = Array.from(parent.children)
		.filter((child): child is HTMLElement => child instanceof HTMLElement && registered.has(child))
		.map(child => child.getBoundingClientRect())
	if (!first || !second) return 'down'
	return Math.abs(second.left - first.left) > Math.abs(second.top - first.top) ? 'across' : 'down'
}

function getDropPosition(
	e: DragEvent,
	targetEl: HTMLElement,
	sourceId: string | null,
	axis: DropAxis,
): 'before' | 'after' | null {
	const tr = targetEl.getBoundingClientRect()
	const sourceEl = sourceId ? dragRegistry.get(sourceId) : null

	if (axis === 'across') {
		const midX = tr.left + tr.width / 2
		if (!sourceEl) return e.clientX < midX ? 'before' : 'after'
		// Each gap belongs to one tile-half — the half facing the side the row is
		// travelling from — so sweeping across the list never offers one gap twice.
		const sr = sourceEl.getBoundingClientRect()
		return sr.left > tr.left ? (e.clientX < midX ? 'before' : null) : e.clientX >= midX ? 'after' : null
	}

	const midY = tr.top + tr.height / 2
	if (!sourceEl) return e.clientY < midY ? 'before' : 'after'
	const sr = sourceEl.getBoundingClientRect()
	return sr.top > tr.top ? (e.clientY < midY ? 'before' : null) : e.clientY >= midY ? 'after' : null
}

/**
 * `AsyncDirective`, not `Directive`: lit-html notifies a directive of its part's
 * connection only through `_$notifyDirectiveConnectionChanged`, which exists on
 * `AsyncDirective` alone, so `disconnected()`/`reconnected()` on a plain
 * `Directive` subclass never run. Left dead, a removed source keeps its
 * `dragstart` subscription and leaves `dragRegistry` holding a detached element
 * that `axisOf()` and `getDropPosition()` then measure against.
 */
export class DragDirective extends AsyncDirective {
	/** The element whose listeners are attached — undefined between a disconnect and the re-attach. */
	private element?: HTMLElement

	/** The element this directive belongs to, remembered ACROSS a disconnect so a reconnect has something to re-attach to. */
	private host?: HTMLElement

	private id!: string
	private registeredId?: string
	private destroy$ = new Subject<void>()

	update(part: ElementPart, [id]: [string]) {
		this.id = id
		this.host = part.element as HTMLElement

		// An update can arrive while the part is disconnected — lit's own note on
		// `AsyncDirective`. Registering a detached element in a module-level
		// registry then is the very leak the teardown below exists to prevent.
		if (this.isConnected) this.attach(this.host)

		return nothing
	}

	/** Called from `update()` on the first render and from `reconnected()`, which cannot wait for a re-render that may never come. */
	private attach(host: HTMLElement) {
		if (!this.element) {
			const el = host
			this.element = el
			el.draggable = true
			el.style.cursor = 'grab'

			fromEvent<DragEvent>(el, 'dragstart')
				.pipe(
					tap(e => {
						e.stopPropagation()
						e.dataTransfer?.setData('application/json', JSON.stringify({ id: this.id }))
						if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
						el.style.cursor = 'grabbing'
						dragSource$.next(this.id)
						// FLIP capture: snapshot pre-drop rects of siblings for the animate-on-drop below
						preDropRects.clear()
						const parent = el.parentElement
						if (parent) {
							for (const [regId, reg] of dragRegistry) {
								if (reg.parentElement === parent) preDropRects.set(regId, reg.getBoundingClientRect())
							}
						}
					}),
					// Defer lift to next frame so browser captures ghost at normal state
					observeOn(animationFrameScheduler),
					tap(() => {
						if (!reducedMotion$.value) {
							el.style.transition = 'transform 150ms ease, box-shadow 150ms ease, opacity 150ms ease'
							el.style.transform = 'scale(1.03)'
							el.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)'
							el.style.opacity = '0.6'
							el.style.zIndex = '1000'
							el.style.pointerEvents = 'none'
						}
					}),
					// dragend is a natural side effect of dragstart — one per session
					switchMap(() =>
						fromEvent<DragEvent>(el, 'dragend').pipe(
							take(1),
							tap(() => {
								const pendingDrop = linePosition$.value

								el.style.removeProperty('transition')
								el.style.removeProperty('transform')
								el.style.removeProperty('box-shadow')
								el.style.removeProperty('opacity')
								el.style.removeProperty('z-index')
								el.style.removeProperty('pointer-events')
								el.style.cursor = 'grab'

								if (pendingDrop) {
									const { target, destinationId, position } = pendingDrop
									target.dispatchEvent(
										new CustomEvent('drop', {
											detail: { source: this.id, destination: destinationId, position },
											bubbles: true,
											composed: true,
										}),
									)
									// FLIP: double rAF waits for Lit render + browser layout
									timer(0, 0, animationFrameScheduler)
										.pipe(skip(1), take(1))
										.subscribe(() => {
											if (reducedMotion$.value || preDropRects.size === 0) return
											for (const [flipId, oldRect] of preDropRects) {
												const reg = dragRegistry.get(flipId)
												if (!reg) continue
												const newRect = reg.getBoundingClientRect()
												const dx = oldRect.left - newRect.left
												const dy = oldRect.top - newRect.top
												if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue
												reg.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0,0)' }], {
													duration: SPRING_SNAPPY.duration,
													easing: SPRING_SNAPPY.easingFallback,
													fill: 'none',
												})
											}
											preDropRects.clear()
										})
								}

								dragSource$.next(null)
								linePosition$.next(null)
							}),
						),
					),
					takeUntil(this.destroy$),
				)
				.subscribe()
		}

		// Keep registry in sync (element may be reused with different id)
		if (this.registeredId && this.registeredId !== this.id) {
			dragRegistry.delete(this.registeredId)
		}
		dragRegistry.set(this.id, host)
		this.registeredId = this.id
	}

	override disconnected() {
		this.destroy$.next()
		this.destroy$.complete()
		if (this.registeredId) dragRegistry.delete(this.registeredId)
		this.element = undefined
	}

	override reconnected() {
		this.destroy$ = new Subject<void>()
		// Re-attached here rather than on the next update: a part reconnecting does
		// not itself re-render, so a source waiting for that update stays inert.
		if (this.host) this.attach(this.host)
	}

	render(id: string) {
		void id
		return nothing
	}
}

export const drag = directive(DragDirective)

/**
 * dragover/drop only exist while a drag is active — both directives derive from
 * dragSource$. `AsyncDirective` for `DragDirective`'s reason, and with more at
 * stake: `dragSource$` is a module-level `BehaviorSubject`, so a removed target
 * whose teardown never runs stays subscribed to it for the life of the page.
 */
export class DropDirective extends AsyncDirective {
	private element?: HTMLElement
	private host?: HTMLElement
	private destinationId!: string
	private destroy$ = new Subject<void>()

	update(part: ElementPart, [destinationId]: [string]) {
		this.destinationId = destinationId
		this.host = part.element as HTMLElement

		// An update while the part is disconnected must not open a subscription to
		// a module-level subject on a detached element's behalf.
		if (this.isConnected) this.attach(this.host)

		return nothing
	}

	/** Called from `update()` on the first render and from `reconnected()`, which cannot wait for a re-render that may never come. */
	private attach(host: HTMLElement) {
		if (!this.element) {
			const el = host
			this.element = el

			fromEvent<DragEvent>(el, 'dragenter')
				.pipe(
					tap(e => {
						e.preventDefault()
					}),
					takeUntil(this.destroy$),
				)
				.subscribe()

			fromEvent<DragEvent>(el, 'dragover')
				.pipe(
					tap(e => {
						e.preventDefault()
						if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
					}),
					takeUntil(this.destroy$),
				)
				.subscribe()

			fromEvent<DragEvent>(el, 'drop')
				.pipe(
					tap(e => {
						e.preventDefault()
					}),
					takeUntil(this.destroy$),
				)
				.subscribe()

			// dragSource$ is the entry — dragover/drop only exist while a drag is active
			dragSource$
				.pipe(
					switchMap(sourceId => {
						if (!sourceId || sourceId === this.destinationId) {
							linePosition$.next(null)
							return EMPTY
						}
						// Once per gesture: the list this target sits in holds its layout for
						// the whole drag, so re-measuring it per dragover frame buys nothing
						// and costs a forced layout on each one.
						const axis = axisOf(el)
						return fromEvent<DragEvent>(el, 'dragover').pipe(
							tap(e => {
								e.preventDefault()
								if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
							}),
							tap(e => {
								const position = getDropPosition(e, el, sourceId, axis)
								linePosition$.next(position ? { target: el, destinationId: this.destinationId, position, axis } : null)
							}),
						)
					}),
					takeUntil(this.destroy$),
				)
				.subscribe()
		}
	}

	override disconnected() {
		this.destroy$.next()
		this.destroy$.complete()
		this.element = undefined
	}

	override reconnected() {
		this.destroy$ = new Subject<void>()
		// Re-attached here rather than on the next update: a part reconnecting does
		// not itself re-render, so a target waiting for that update receives nothing.
		if (this.host) this.attach(this.host)
	}

	render(destinationId: string) {
		void destinationId
		return nothing
	}
}

export const drop = directive(DropDirective)
